import { z } from "zod";

import { pageRefSchema, reviewStateSchema } from "./schema";

const formalTaskIdSchema = z.string().regex(/^T(0[1-9]|1[0-9])$/);
const reviewSequenceIdSchema = z.string().min(1);
const claimIdSchema = z.string().min(1);

const variantTaskBaseSchema = z.object({
  id: z.string().regex(/^V-T(0[1-9]|1[0-9])-\d{2}$/),
  sourceTaskId: formalTaskIdSchema,
  sceneId: z.string().regex(/^M[1-6]S[1-4]$/),
  title: z.string().min(1),
  stem: z.string().min(1),
  hint: z.string().min(1),
  explanation: z.string().min(1),
  pageRefs: z.array(pageRefSchema).min(1),
  reviewState: reviewStateSchema,
  formal: z.literal(false),
});

const variantChoiceOptionSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
  feedback: z.string().min(1),
  misconceptionCode: z.string().min(1).optional(),
});

export const remediationVariantSingleChoiceSchema = variantTaskBaseSchema
  .extend({
    type: z.literal("single_choice"),
    presentation: z.literal("cards"),
    options: z.array(variantChoiceOptionSchema).min(2),
    correctOptionId: z.string().min(1),
  })
  .superRefine((task, context) => {
    const optionIds = task.options.map((option) => option.id);
    if (new Set(optionIds).size !== optionIds.length) {
      context.addIssue({
        code: "custom",
        message: "补救变式选择题的选项ID必须唯一。",
        path: ["options"],
      });
    }
    if (!optionIds.includes(task.correctOptionId)) {
      context.addIssue({
        code: "custom",
        message: "补救变式选择题的正确答案必须引用现有选项。",
        path: ["correctOptionId"],
      });
    }
  });

const sortErrorFeedbackSchema = z.object({
  id: z.string().regex(/^V-T(0[1-9]|1[0-9])-FB-\d{2}$/),
  whenOrder: z.array(z.string().min(1)).min(2),
  misconceptionCode: z.string().min(1),
  feedback: z.string().min(1),
  reviewSequenceIds: z.array(reviewSequenceIdSchema).min(1),
  claimIds: z.array(claimIdSchema).min(1),
  pageRefs: z.array(pageRefSchema).min(1),
});

export const remediationVariantDragSortSchema = variantTaskBaseSchema
  .extend({
    type: z.literal("drag_sort"),
    items: z.array(z.object({ id: z.string().min(1), text: z.string().min(1) })).min(2),
    correctOrder: z.array(z.string().min(1)).min(2),
    errorFeedback: z.array(sortErrorFeedbackSchema).min(1),
  })
  .superRefine((task, context) => {
    const itemIds = task.items.map((item) => item.id);
    const isPermutation = (candidate: readonly string[]) =>
      candidate.length === itemIds.length &&
      new Set(candidate).size === itemIds.length &&
      candidate.every((id) => itemIds.includes(id));

    if (new Set(itemIds).size !== itemIds.length || !isPermutation(task.correctOrder)) {
      context.addIssue({
        code: "custom",
        message: "补救变式排序题的正确顺序必须且只能完整引用全部唯一项目。",
        path: ["correctOrder"],
      });
    }

    const correctOrderKey = task.correctOrder.join("|");
    const seenIncorrectOrders = new Set<string>();
    for (const [index, feedback] of task.errorFeedback.entries()) {
      const orderKey = feedback.whenOrder.join("|");
      if (!isPermutation(feedback.whenOrder)) {
        context.addIssue({
          code: "custom",
          message: `${feedback.id}的错误顺序必须完整引用全部排序项目。`,
          path: ["errorFeedback", index, "whenOrder"],
        });
      }
      if (orderKey === correctOrderKey) {
        context.addIssue({
          code: "custom",
          message: `${feedback.id}不能把正确顺序登记为错误反馈。`,
          path: ["errorFeedback", index, "whenOrder"],
        });
      }
      if (seenIncorrectOrders.has(orderKey)) {
        context.addIssue({
          code: "custom",
          message: `${feedback.id}重复登记了同一错误顺序。`,
          path: ["errorFeedback", index, "whenOrder"],
        });
      }
      seenIncorrectOrders.add(orderKey);
    }
  });

export const remediationVariantTaskSchema = z.union([
  remediationVariantSingleChoiceSchema,
  remediationVariantDragSortSchema,
]);

const remediationStepSchema = z.object({
  id: z.string().regex(/^R-T(0[1-9]|1[0-9])-S\d{2}$/),
  order: z.number().int().positive(),
  kind: z.enum(["evidence_revisit", "guided_contrast", "knowledge_reframe"]),
  durationSeconds: z.number().int().min(10).max(60),
  title: z.string().min(1),
  instruction: z.string().min(1),
  expectedObservation: z.string().min(1),
  reviewSequenceIds: z.array(reviewSequenceIdSchema).min(1),
  claimIds: z.array(claimIdSchema).min(1),
  pageRefs: z.array(pageRefSchema).min(1),
});

const misconceptionSchema = z.object({
  code: z.string().min(1),
  diagnosis: z.string().min(1),
  feedback: z.string().min(1),
  reviewSequenceIds: z.array(reviewSequenceIdSchema).min(1),
  claimIds: z.array(claimIdSchema).min(1),
  pageRefs: z.array(pageRefSchema).min(1),
});

export const remediationBlueprintSchema = z
  .object({
    id: z.string().regex(/^RBP-T(0[1-9]|1[0-9])-\d{2}$/),
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    sourceTaskId: formalTaskIdSchema,
    sceneId: z.string().regex(/^M[1-6]S[1-4]$/),
    title: z.string().min(1),
    trigger: z.literal("first_incorrect_or_hint_used"),
    estimatedSeconds: z.number().int().min(60).max(90),
    misconceptionCodes: z.array(z.string().min(1)).min(1),
    misconceptions: z.array(misconceptionSchema).min(1),
    reviewSequenceIds: z.array(reviewSequenceIdSchema).min(1),
    claimIds: z.array(claimIdSchema).min(1),
    pageRefs: z.array(pageRefSchema).min(1),
    steps: z.array(remediationStepSchema).min(2),
    variantTask: remediationVariantTaskSchema,
    reviewState: reviewStateSchema,
  })
  .superRefine((blueprint, context) => {
    const stepSeconds = blueprint.steps.reduce((total, step) => total + step.durationSeconds, 0);
    if (stepSeconds !== blueprint.estimatedSeconds) {
      context.addIssue({
        code: "custom",
        message: "补救步骤时长之和必须等于蓝图预计时长。",
        path: ["steps"],
      });
    }

    const stepOrders = blueprint.steps.map((step) => step.order);
    if (
      new Set(stepOrders).size !== stepOrders.length ||
      stepOrders.some((order, index) => order !== index + 1)
    ) {
      context.addIssue({
        code: "custom",
        message: "补救步骤必须从1开始连续排序。",
        path: ["steps"],
      });
    }

    const misconceptionCodes = blueprint.misconceptions.map((item) => item.code);
    if (
      new Set(blueprint.misconceptionCodes).size !== blueprint.misconceptionCodes.length ||
      new Set(misconceptionCodes).size !== misconceptionCodes.length ||
      blueprint.misconceptionCodes.length !== misconceptionCodes.length ||
      blueprint.misconceptionCodes.some((code) => !misconceptionCodes.includes(code))
    ) {
      context.addIssue({
        code: "custom",
        message: "misconceptionCodes必须与具体误区反馈一一对应。",
        path: ["misconceptionCodes"],
      });
    }

    if (
      blueprint.variantTask.sourceTaskId !== blueprint.sourceTaskId ||
      !blueprint.variantTask.id.startsWith(`V-${blueprint.sourceTaskId}-`) ||
      blueprint.variantTask.sceneId !== blueprint.sceneId
    ) {
      context.addIssue({
        code: "custom",
        message: "变式题必须关联同一正式任务和场景，并使用非正式V-任务ID。",
        path: ["variantTask"],
      });
    }

    if (
      (blueprint.sourceTaskId === "T01" && blueprint.variantTask.type !== "single_choice") ||
      (blueprint.sourceTaskId === "T04" && blueprint.variantTask.type !== "drag_sort")
    ) {
      context.addIssue({
        code: "custom",
        message: "T01补救变式必须为单选，T04补救变式必须为拖拽排序。",
        path: ["variantTask", "type"],
      });
    }

    const reviewSequenceIds = new Set(blueprint.reviewSequenceIds);
    const claimIds = new Set(blueprint.claimIds);
    const validateReviewRefs = (
      owner: { reviewSequenceIds: readonly string[]; claimIds: readonly string[] },
      path: (string | number)[],
    ) => {
      if (owner.reviewSequenceIds.some((id) => !reviewSequenceIds.has(id))) {
        context.addIssue({
          code: "custom",
          message: "局部回看节点必须包含在蓝图reviewSequenceIds中。",
          path: [...path, "reviewSequenceIds"],
        });
      }
      if (owner.claimIds.some((id) => !claimIds.has(id))) {
        context.addIssue({
          code: "custom",
          message: "局部主张必须包含在蓝图claimIds中。",
          path: [...path, "claimIds"],
        });
      }
    };

    blueprint.steps.forEach((step, index) => {
      validateReviewRefs(step, ["steps", index]);
    });
    blueprint.misconceptions.forEach((item, index) => {
      validateReviewRefs(item, ["misconceptions", index]);
    });

    if (blueprint.variantTask.type === "single_choice") {
      for (const [index, option] of blueprint.variantTask.options.entries()) {
        if (
          option.id !== blueprint.variantTask.correctOptionId &&
          (!option.misconceptionCode ||
            !blueprint.misconceptionCodes.includes(option.misconceptionCode))
        ) {
          context.addIssue({
            code: "custom",
            message: "每个错误选项必须关联蓝图内的具体误区编码。",
            path: ["variantTask", "options", index, "misconceptionCode"],
          });
        }
      }
    } else {
      for (const [index, feedback] of blueprint.variantTask.errorFeedback.entries()) {
        if (!blueprint.misconceptionCodes.includes(feedback.misconceptionCode)) {
          context.addIssue({
            code: "custom",
            message: "排序错误反馈必须关联蓝图内的具体误区编码。",
            path: ["variantTask", "errorFeedback", index, "misconceptionCode"],
          });
        }
        validateReviewRefs(feedback, ["variantTask", "errorFeedback", index]);
      }
    }
  });

export const remediationBlueprintsSchema = z.array(remediationBlueprintSchema);

export type RemediationVariantSingleChoice = z.infer<typeof remediationVariantSingleChoiceSchema>;
export type RemediationVariantDragSort = z.infer<typeof remediationVariantDragSortSchema>;
export type RemediationVariantTask = z.infer<typeof remediationVariantTaskSchema>;
export type RemediationBlueprint = z.infer<typeof remediationBlueprintSchema>;
